import Translations from '@helpers/translations'
import {
  useForm,
  FormProvider,
  SubmitHandler,
  useFormState
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import useTurnstile from '@helpers/hooks/useTurnstile'
import { z } from 'zod'
import { InputField } from '../../../ui/formElements/form/InputField'
import styles from './styles.module.scss'

type EmailFormProps = {
  onSubmit: (email: string, turnstileToken?: string) => void | Promise<void>
  children: (name: string, isValid: boolean) => React.ReactNode
  classes?: string | undefined
  placeholder?: string
  honeypot?: boolean
  turnstileAction?: string
  onBotDetected?: () => void
}

const emailSchema = z.object({
  newsletter: InputField.getSchema('email', true),
  nl_hp: z.string().optional()
})

type EmailFormData = z.infer<typeof emailSchema>

export function EmailForm({
  onSubmit,
  classes,
  placeholder,
  honeypot,
  turnstileAction,
  onBotDetected,
  children
}: EmailFormProps) {
  const {
    isReady: turnstileReady,
    withToken: withTurnstileToken,
    widget: turnstileWidget
  } = useTurnstile(turnstileAction)
  const methods = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { newsletter: '', nl_hp: '' }
  })

  const { handleSubmit, watch, control, register } = methods
  const { isValid } = useFormState({ control })
  const email = watch('newsletter')

  const submitHandler: SubmitHandler<EmailFormData> = async data => {
    if (honeypot && data.nl_hp) {
      onBotDetected?.()
      return
    }

    await withTurnstileToken(token => onSubmit(data.newsletter, token))
  }

  return (
    <FormProvider {...methods}>
      <form
        method="post"
        className={classes}
        onSubmit={handleSubmit(submitHandler)}
        noValidate
      >
        <InputField<EmailFormData>
          placeholder={
            placeholder ||
            Translations.localise(
              'common.newsletter-modal.form.email-placeholder'
            )
          }
          name="newsletter"
          type="email"
          required
          label="Email"
        />
        {honeypot && (
          <input
            {...register('nl_hp')}
            type="text"
            className={styles.honeypot}
            tabIndex={-1}
            aria-hidden="true"
            autoComplete="off"
          />
        )}
        {turnstileWidget}
        {children(email, isValid && turnstileReady)}
      </form>
    </FormProvider>
  )
}
